import { cn } from "../../lib/utils";

interface SpinnerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "sm" | "md" | "lg";
}

/**
 * The ring used to be drawn with `border` / `border-2` plus a coloured top
 * edge — banned under `docs/borderless-contract.md` §5, and a live reference to
 * the deprecated `--color-line`. Same spinner, drawn with SVG strokes so no
 * border is involved; identical construction to `PageLoader`. `strokeWidth` is
 * the old border width scaled into the 24-unit viewBox, so the ring weight at
 * each size is unchanged.
 */
const sizeClasses: Record<NonNullable<SpinnerProps["size"]>, string> = {
  sm: "size-3",
  md: "size-3.5",
  lg: "size-4",
};

const strokeWidths: Record<NonNullable<SpinnerProps["size"]>, number> = {
  sm: 2,
  md: 3.5,
  lg: 3,
};

function Spinner({ className, size = "md", ...props }: SpinnerProps) {
  const strokeWidth = strokeWidths[size];

  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn(
        "inline-flex shrink-0 items-center justify-center text-text-tertiary",
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        className="size-full animate-spin"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeOpacity="0.25"
          strokeWidth={strokeWidth}
        />
        <path
          d="M22 12a10 10 0 0 0-10-10"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
      </svg>
    </div>
  );
}

export { Spinner };
